// Third Party library imports
import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { lastValueFrom } from 'rxjs';
import { Guid } from 'guid-typescript';

// Zopup Library imports
import {
    BizError,
    CompanyDto, CompanyDtoWebApiResponse, CompanyCreateCommand,
    TenantDto,
    CompanyUpdateCommand,
    CompanyEditDto,
    CompanyCompactDto,
    CompanyPagedListQuery
} from '@zopup/zop-types';
import { StorageKey, StorageService, X_APP_ID } from '@zopup/zop-shared';

//Internal Projects imports
import { CompanyService } from './company.service';

type CompanyState = {
    companies: CompanyCompactDto[];
    selectedCompany: CompanyDto | undefined;
    isLoading: boolean;
    errors: BizError[];
    query: CompanyPagedListQuery;
};

const initialState: CompanyState = {
    companies: [],
    selectedCompany: {},
    isLoading: false,
    errors: [],
    query: {isActive: true },
};

export const CompanyStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((store,
        companyService = inject(CompanyService),
        _storageService = inject(StorageService)) => ({
        onSelectCompany(company: CompanyDto): void {
            patchState(store, { selectedCompany: company });
        },
        updateQuery(query: string): void {
            patchState(store, (state) => ({
                query: { ...state.query, query },
            }));
        },
        updateOrder(order: 'asc' | 'desc'): void {
            patchState(store, (state) => ({
                query: { ...state.query, order },
            }));
        },

        getList: async () => {
            try{
                const xTenantId = _storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                patchState(store, { isLoading: true });
                const query = store.query();
                const companies = await lastValueFrom(
                    companyService.companyGet(xTenantId, query.isActive, query.companyType, query.verificationStatus));
                if (companies.isSuccess) {
                    patchState(store, {
                        companies: companies.result!,
                        isLoading: false,
                    });
                } else {
                    patchState(store, {
                        errors: companies.errors!,
                        isLoading: false,
                    });
                }
            }catch (error) {
                patchState(store, { isLoading: false });
                console.error('Exception in company.store => getList()');
                console.error(error);
            }
        },

        //Get the selected company Details
        getById: async (companyId?: string) => {
            if(companyId){

                try{
                    const xTenantId = _storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                    patchState(store, { isLoading: true });
                    const response = await lastValueFrom(
                        companyService.companyIdGet(companyId, xTenantId)
                    );
                    if (response?.isSuccess) {
                        //Update the store
                        patchState(store, {
                            selectedCompany: response.result,
                            isLoading: false,
                        });
                    } else {
                        patchState(store, {
                            selectedCompany: undefined,
                            errors: response?.errors,
                            isLoading: false,
                        });
                    }
                }catch (error) {
                    patchState(store, { isLoading: false });
                    console.error('Exception in company.store => getList()');
                    console.error(error);
                }
            }
        },

        // Create new company
        add: async (
            createCommand: CompanyCreateCommand,
        ): Promise<CompanyDtoWebApiResponse> => {

            let companyResponse!:CompanyDtoWebApiResponse;
            try {
                patchState(store, { isLoading: true });
                const xTenantId = _storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                const xRequestId = Guid.create().toString();

                companyResponse = await lastValueFrom(
                    companyService.companyPost(xTenantId, xRequestId, createCommand)
                );
                patchState(store, { isLoading: false });
            } catch (error) {
                patchState(store, { isLoading: false });
                console.error('Exception in company.store => add()');
                console.error(error);
                companyResponse = {
                    isSuccess: false,
                    message: 'ERROR: Company Creation Failed',
                    errors: [],
                    result: undefined
                };
            }
            return companyResponse;
        },

        update: async (
            selectedCompany: CompanyEditDto,
        ): Promise<CompanyDtoWebApiResponse> => {
            const companyUpdateCommand: CompanyUpdateCommand = {
                company: selectedCompany
            };

            let companyResponse!:CompanyDtoWebApiResponse;
            try {
                patchState(store, { isLoading: true });
                const xTenantId = _storageService.getItem<TenantDto>(StorageKey.tenant)?.id!;
                companyResponse = await lastValueFrom(
                    companyService.companyPut(xTenantId, companyUpdateCommand)
                );
                if (companyResponse.isSuccess) {
                    _storageService.setItem<CompanyDto>(StorageKey.company, companyResponse.result!);
                    patchState(store, { isLoading: false });
                }
            } catch (error) {
                patchState(store, { isLoading: false });
                console.error('Exception in company.store => update()');
                console.error(error);
                companyResponse = {
                    isSuccess: false,
                    message: 'ERROR: Company Updation Failed',
                    errors: [],
                    result: undefined
                };
            }
            return companyResponse;
        },
    }))
);
